import type { AdminInfo, DomainWithProjects } from '@protoss/shared';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspace } from './WorkspaceContext';

/**
 * Current application context - which domain/project is active
 */
interface AppContextValue {
  /** Currently selected domain (from URL params) */
  currentDomain: DomainWithProjects | undefined;

  /** Currently selected project (from URL params) */
  currentProject:
    | {
        uuid: string;
        name: string;
        description: string | null;
        domainName: string;
        domainUuid: string;
        moduleKey: string;
        admins: AdminInfo[];
        managers: AdminInfo[];
      }
    | undefined;

  /** Whether we're viewing a domain detail page */
  isDomainView: boolean;

  /** Whether we're viewing a project detail page */
  isProjectView: boolean;

  /** Breadcrumb path for current view */
  breadcrumb: string;
}

const AppContext = React.createContext<AppContextValue | null>(null);

/**
 * AppContextProvider - provides current domain/project context
 *
 * Automatically tracks the current domain and project based on URL params,
 * providing easy access to the current context throughout the application.
 *
 * Architecture:
 * - Consumes WorkspaceContext for domain/project data
 * - Tracks URL params to determine current selection
 * - Computes breadcrumb paths automatically
 * - Provides boolean helpers for view detection
 */
export function AppContextProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { domains } = useWorkspace();

  // Parse domain/project UUIDs from the pathname directly.
  // useParams() cannot be used here because AppContextProvider sits outside <Routes>.
  const domainMatch = location.pathname.match(/^\/do\/domain\/([^/]+)/);
  const domainUuid = domainMatch && domainMatch[1] !== 'new' ? domainMatch[1] : undefined;

  const projectMatch = location.pathname.match(/^\/do\/project\/([^/]+)/);
  const projectUuid = projectMatch && projectMatch[1] !== 'new' ? projectMatch[1] : undefined;

  const value = React.useMemo(() => {
    // Find current domain from URL params
    const currentDomain = domainUuid ? domains.find((d) => d.uuid === domainUuid) : undefined;

    // Find current project from URL params
    const currentProject = projectUuid
      ? domains
          .flatMap((d) =>
            d.projects.map((p) => ({
              ...p,
              domainName: d.name,
              domainUuid: d.uuid,
            }))
          )
          .find((p) => p.uuid === projectUuid)
      : undefined;

    // Determine view type
    const isDomainView = !!domainUuid;
    const isProjectView = !!projectUuid;

    // Compute breadcrumb
    let breadcrumb = '';
    if (currentProject) {
      breadcrumb = `${currentProject.domainName} > ${currentProject.name}`;
    } else if (currentDomain) {
      breadcrumb = 'Domain';
    }

    return {
      currentDomain,
      currentProject,
      isDomainView,
      isProjectView,
      breadcrumb,
    };
  }, [domainUuid, projectUuid, domains]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * useAppContext - access current domain/project context
 *
 * Returns the current domain and project based on URL routing,
 * along with helper flags and computed breadcrumbs.
 *
 * @example
 * ```tsx
 * function ProjectHeader() {
 *   const { currentProject, breadcrumb } = useAppContext();
 *
 *   if (!currentProject) return null;
 *
 *   return (
 *     <header>
 *       <h1>{currentProject.name}</h1>
 *       <p>{breadcrumb}</p>
 *     </header>
 *   );
 * }
 * ```
 */
export function useAppContext() {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppContextProvider');
  }
  return context;
}
